<template>
  <form @submit.prevent="handleSubmit">
    <div class="flex justify-center flex-wrap gap-8 mx-4">
      <div class="w-[400px] flex flex-col flex-col gap-3">
        <div>
          <label
            v-if="!showNoOfferingsFoundMessage"
            class="block text-sm font-semibold leading-6 text-gray-900 mb-1"
          >
            {{ $t('Choose a date to see available tours') }}
          </label>
          <n-collapse
            :expanded-names="isOpenCalendar ? 'calendar' : ''"
            display-directive="show"
            @item-header-click="isOpenCalendar = !isOpenCalendar"
          >
            <n-collapse-item name="calendar">
              <template #header>
                <Input
                  v-if="offerings?.length > 0 && !isOpenCalendar"
                  :model-value="friendlySelectedDate"
                  class="cursor-pointer -mt-1"
                  readonly
                >
                  <template #icon-right>
                    <CalendarIcon class="h-4 inline" />
                  </template>
                </Input>
              </template>
              <div
                v-show="showNoOfferingsFoundMessage"
                class="mt-3 flex justify-center"
              >
                <span>
                  <span class="text-md">{{
                    $t(
                      'There are no available dates at the moment. The schedule will be updated soon.',
                    )
                  }}</span>
                </span>
              </div>
              <div
                class="datepicker-container mt-1"
                v-if="!showNoOfferingsFoundMessage"
              >
                <Calendar
                  v-model:baseDateTime="calendarBaseDateTime"
                  :available="datesWithOfferings"
                  :loading="
                    (fetchingOfferings || isHydratingOfferings) &&
                    !showNoOfferingsFoundMessage
                  "
                  disable-past-dates
                  @update:modelValue="handleSelectedDateObject"
                  @update-month-year="fetchUpdatedOfferings"
                >
                  <template #day="{ day, date }">
                    {{ day }}
                  </template>
                </Calendar>
                <div
                  v-if="v$.selectedDateObject?.$errors?.length"
                  class="text-red-500"
                >
                  <div
                    v-for="error of v$.selectedDateObject.$errors"
                    :key="error.$uid"
                    class="input-errors"
                  >
                    <div class="error-msg">{{ error.$message }}</div>
                  </div>
                </div>
              </div>
            </n-collapse-item>
          </n-collapse>
        </div>
        <template v-if="!isOpenCalendar">
          <div>
            <p class="block text-sm font-semibold leading-6 text-gray-900 mb-1">
              {{ $t('Choose a tour') }}
            </p>
            <Radio
              ref="offeringSelection"
              v-model="selectedOffering"
              :errors="v$.selectedOffering.$errors"
              :options="internalOfferingsOnSelectedDateInput"
              @change="handleOfferingSelectionChange"
            >
              <template #description="{ selectedOption }">
                <div class="my-2 p-3 rounded border text-xs">
                  <h4 class="font-bold mb-1">
                    {{ $t('Description') }}
                  </h4>
                  <p class="mb-2" v-if="selectedOption.price > 0">
                    <strong>Note:</strong> This is a paid tour. You will be
                    asked to make a payment in the next step to confirm your
                    booking.
                  </p>
                  <p class="whitespace-pre-wrap">
                    {{ selectedOption.description }}
                  </p>
                  <p
                    class="whitespace-pre-wrap mt-2"
                    v-if="selectedOption.meeting_point_address"
                  >
                    {{ $t('Meeting point') }}:<br />
                    {{ selectedOption.meeting_point_address }}
                  </p>
                </div>
              </template>
            </Radio>
          </div>
          <template v-if="partnerOfferingsOnSelectedDateInput?.length > 0">
            <div>
              <p
                class="block text-sm font-semibold leading-6 text-gray-900 mb-1"
              >
                {{ $t('Trusted partner tours') }}
              </p>
              <Radio
                ref="offeringSelection"
                v-model="selectedOffering"
                :errors="v$.selectedOffering.$errors"
                :options="partnerOfferingsOnSelectedDateInput"
                @change="handleOfferingSelectionChange"
              >
                <template #description="{ selectedOption }">
                  <div class="my-2 p-3 rounded border text-xs">
                    <h4 class="font-bold mb-1">
                      {{ $t('Description') }}
                    </h4>
                    <p class="whitespace-pre-wrap">
                      {{ selectedOption.description }}
                    </p>
                  </div>
                </template>
              </Radio>
            </div>
          </template>
        </template>
      </div>
      <div class="w-[400px] flex flex-col flex-col gap-3">
        <div v-if="isAdmin" class="mb-5">
          <Select
            v-model="externalProvider"
            :options="[
              { value: '', label: 'None' },
              { value: 'civitatis', label: 'Civitatis' },
              { value: 'freetour.com', label: 'Freetour.com' },
              { value: 'freetour.tours', label: 'Freetour.tours' },
              { value: 'freetourbyfoot', label: 'FreetourByFoot' },
              { value: 'guru', label: 'Guru' },
              { value: 'yoorney', label: 'Yoorney' },
            ]"
          >
            <template #label>External booking platform</template>
            <template #placeholder>Select one (optional)</template>
          </Select>
        </div>
        <div v-if="selectedOfferingLanguages?.length > 1">
          <p class="block text-sm font-semibold leading-6 text-gray-900 mb-1">
            {{ $t('Choose a language') }}
          </p>
          <div class="rounded-md ring-1 ring-inset ring-gray-300 p-3">
            <n-radio-group
              v-model:value="selectedOfferingIdFromLanguage"
              name="radiogroup"
            >
              <div class="flex flex-col gap-3">
                <n-radio
                  v-for="languageOption in selectedOfferingLanguages"
                  :key="languageOption.value"
                  :value="languageOption.value"
                  :label="languageOption.label"
                />
                <span
                  class="text-red-500"
                  v-if="v$.selectedLanguage.$errors?.[0]?.$message"
                  >{{ v$.selectedLanguage.$errors?.[0]?.$message }}</span
                >
              </div>
            </n-radio-group>
          </div>
        </div>
        <n-collapse-transition
          :show="isSelectedOfferingFull"
          display-directive="show"
        >
          <n-alert
            v-if="isSelectedOfferingFull"
            class="max-w-[400px]"
            :title="$t('Tour is fully booked')"
            type="warning"
          >
            {{
              $t(
                'Unfortunately, the selected tour is fully booked. Please choose a different tour or time.',
              )
            }}

            <br />
            <br />
            {{
              $t(
                'You are also welcome to meet us at the designated meeting point for the tour; however, a spot on the tour is not guaranteed.',
              )
            }}
          </n-alert>
        </n-collapse-transition>
        <Input
          ref="nameTemplateRef"
          v-model="v$.name.$model"
          :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
          :errors="v$.name.$errors"
          required
          @input="touchDateAndTourFields"
        >
          <template #label>{{ $t('Your name') }} </template>
          <template #icon-right>
            <UserIcon class="h-4 inline" />
          </template>
        </Input>
        <div class="">
          <Input
            ref="emailTemplateRef"
            v-model="email"
            :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
            :errors="v$.email?.$errors"
            placeholder="example@example.com"
            :required="isEmailRequired"
            type="email"
            @blur="v$.email.$touch"
            @input="touchDateAndTourFields"
          >
            <template #label
              >{{ $t('Email address') }}
              {{ isEmailRequired ? '' : `(${$t('optional')})` }}</template
            >
            <template #icon-right>
              <EnvelopeIcon class="h-4 inline" />
            </template>
            <template v-if="!isAdmin" #assistance>
              {{
                $t(
                  'This will be used to send you a confirmation, reminder, and follow-up.',
                )
              }}
            </template>
          </Input>
        </div>
        <div class="flex gap-4">
          <div class="w-full">
            <Select
              v-model="guestCount"
              :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
              :options="availableAdultOptions"
            >
              <template #label>{{ $t('Adults') }} </template>
              <template #placeholder>{{ $t('Please select') }}</template>
              <template #assistance>
                <span>
                  {{
                    $t('Ages 17+', {
                      max: Math.max(0, maxSeatsPerBooking),
                    })
                  }}
                </span>
              </template>
            </Select>
          </div>
          <div class="w-full">
            <Select
              v-model="guestCountChildren"
              :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
              :options="availableChildrenOptions"
            >
              <template #label>{{ $t('Children') }} </template>
              <template #placeholder>{{ $t('Please select') }}</template>
              <template #assistance>
                <span>
                  {{
                    $t('Ages 0-16', {
                      max: Math.max(0, maxSeatsPerBooking),
                    })
                  }}
                </span>
              </template>
            </Select>
          </div>
        </div>
        <div v-if="!isAdmin" class="">
          <div class="text-slate-500 max-w-[350px] pb-3">
            <template
              v-if="selectedOfferingObject?.children_count_toward_max_seats"
            >
              <template
                v-if="
                  !selectedOfferingObject ||
                  maxSeatsPerBooking < (selectedOfferingObject?.seats || 0)
                "
              >
                {{
                  $t(
                    'The maximum group size for this tour is {max} people (adults and children combined).',
                    {
                      max: maxSeatsPerBooking || 0,
                    },
                  )
                }}
              </template>
              <template v-else>
                {{
                  selectedOfferingObject?.children_count_toward_total_spaces
                    ? $t(
                        'This tour has {max} seats available (adults and children combined).',
                        {
                          max: maxSeatsPerBooking || 0,
                        },
                      )
                    : $t('This tour has {max} seats available for adults.', {
                        max: maxSeatsPerBooking || 0,
                      })
                }}
              </template>
            </template>
            <template v-else>
              <template
                v-if="
                  !selectedOfferingObject ||
                  maxSeatsPerBooking < (selectedOfferingObject?.seats || 0)
                "
              >
                {{
                  $t('The maximum group size for this tour is {max} adults.', {
                    max: maxSeatsPerBooking || 0,
                  })
                }}
              </template>
              <template v-else>
                {{
                  $t('This tour has {max} seats available for adults.', {
                    max: maxSeatsPerBooking || 0,
                  })
                }}
              </template>
            </template>
          </div>
          <div class="text-slate-500 max-w-[350px] pb-3">
            {{
              $t(
                'The group size limit is enforced and making multiple bookings to bypass this rule is not allowed.',
              )
            }}

            <div v-if="guestCount == maxSeatsPerBooking" class="mt-2">
              {{
                $t('If you are a group of {max} or more', {
                  max: (maxSeatsPerBooking || 0) + 1,
                })
              }},
              <n-a
                :href="`mailto:${selectedOfferingObject?.tour?.company?.support_email}`"
                target="_blank"
              >
                {{ $t('please send us an email') }}
              </n-a>
              {{ $t('to coordinate a private tour') }} .
            </div>
          </div>
        </div>
        <div class="" v-if="hasLoadedOfferingsOnce && isPhoneEnabled">
          <InputTelephone
            v-model="phone"
            :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
            :required="isPhoneRequired"
            :errors="phoneErrors"
            @input="touchDateAndTourFields"
          >
            <template #label>{{ $t('Phone number') }} </template>
            <template #icon-right>
              <PhoneIcon class="h-4 inline" />
            </template>
            <template v-if="!isAdmin" #assistance>
              {{
                $t('This will be used to contact you in case of tour issues.')
              }}
            </template>
          </InputTelephone>
        </div>
        <div class="" v-if="isViewingFromOrganisation">
          <Select
            ref="freeTourCommunityMemberTemplateRef"
            v-model="freeTourCommunityMemberReferrer"
            :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
            :options="freeTourCommunityMembers"
            @click="fetchFreeTourCommunityMembers"
            @update:modelValue="v$.freeTourCommunityMemberReferrer.$touch"
          >
            <template #label>{{
              $t('Which FTC member referred you to join us?')
            }}</template>
            <template #placeholder>{{
              $t('Select a Free Tour Community member')
            }}</template>
          </Select>
        </div>
        <div class="" v-else-if="isCountryEnabled">
          <Select
            ref="countryTemplateRef"
            v-model="country"
            :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
            :options="countryOptions"
          >
            <template #label>{{ $t('Where do you live?') }}</template>
            <template #placeholder
              >{{ $t('Please select a country') }}
              {{ isAdmin ? '(optional)' : '' }}</template
            >
          </Select>
        </div>
        <div v-if="v$.$errors?.[0]?.$message || isSelectedOfferingFull">
          <NAlert type="error" :show-icon="false">
            <template v-if="isSelectedOfferingFull">
              {{ $t('Unfortunately, the selected tour is fully booked.') }}
              <br />
              {{ $t('Please choose a different tour or time.') }}
              <br />
              {{
                $t(
                  'You are also welcome to meet us at the designated meeting point for the tour; however, a spot on the tour is not guaranteed.',
                )
              }}
            </template>
            <template v-else> {{ v$.$errors?.[0]?.$message }}</template>
          </NAlert>
        </div>
        <div
          class="text-blue-600 cursor-pointer -mb-2"
          v-if="!isCommentFieldOpen"
          @click="toggleCommentField"
        >
          {{ $t('Want to add additional comments?') }}
        </div>
        <Input
          v-if="isCommentFieldOpen"
          ref="commentFieldRef"
          v-model="comments"
          :disabled="isSelectedOfferingFull || showNoOfferingsFoundMessage"
          type="textarea"
        >
          <template #label>{{ $t('Comments') }} </template>
          <template #icon-right>
            <ChatBubbleBottomCenterTextIcon class="h-4 inline" />
          </template>
        </Input>
        <div>
          <ButtonLoading
            class="w-full text-center mt-2 mb-8"
            ref="makeBookingTemplateRef"
            :working="submitting"
            :disabled="
              v$.$errors?.[0]?.$message ||
              isSelectedOfferingFull ||
              showNoOfferingsFoundMessage
            "
            type="submit"
          >
            {{ $t('Make booking') }}
          </ButtonLoading>
          <div class="flex justify-center items-center mb-12">
            <a href="https://robotkong.com" target="_blank" class="text-xs text-gray-500 hover:text-gray-700">
              Booking system by Robotkong
            </a>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>
<script lang="ts" setup>
/**
 * @todo Check if seats should really be disabled if event is full (maybe just the whole form, etc?
 */
import { useVuelidate } from '@vuelidate/core'
import {
  required as requiredValidator,
  email as emailValidator,
  helpers,
} from '@vuelidate/validators'
import '@vuepic/vue-datepicker/dist/main.css'
import { DateTime } from 'luxon'
import countries from 'assets/data/countries.json'
import { breakpointsTailwind, useBreakpoints, useFocus } from '@vueuse/core'
import {
  ChatBubbleBottomCenterTextIcon,
  CalendarIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/vue/24/outline'
import Input from '~/components/form/Input.vue'
import Select from '~/components/form/Select.vue'
import Radio from '~/components/form/Radio.vue'
import ButtonLoading from '~/components/form/ButtonLoading.vue'
import langs from 'langs'
import InputTelephone from '~/components/form/InputTelephone.vue'
import { useFetch } from '#app'
import { PayScheme } from '~/types/PayScheme'
const { t, locale } = useI18n()

const route = useRoute()

const companyName = ref('')
const companyCity = ref('')
const rkRef = ref(route.query.rk_ref || null)

const calendarBaseDateTime = ref(DateTime.now())

definePageMeta({
  layout: 'iframes',
})

useHead({
  title:
    route?.query?.company || ''
      ? `Tours by ${route?.query?.company}`
      : "Robotkong | A booking system that's easy",
  meta: [
    {
      name: 'description',
      content:
        route?.query?.city || ''
          ? `Book walking tours in ${route?.query?.city}`
          : 'A booking system for tours and events allowing you to manage bookings, check-ins, and include a booking form on your website.',
    },
  ],
})
useSeoMeta({
  title:
    route?.query?.company || ''
      ? `Tours by ${route?.query?.company}`
      : "Robotkong | A booking system that's easy",
  ogTitle:
    route?.query?.company || ''
      ? `Tours by ${route?.query?.company}`
      : "Robotkong | A booking system that's easy",
  description:
    route?.query?.city || ''
      ? `Book walking tours in ${route?.query?.city}`
      : 'A booking system for tours and events allowing you to manage bookings, check-ins, and include a booking form on your website.',
  ogDescription:
    route?.query?.city || ''
      ? `Book walking tours in ${route?.query?.city}`
      : 'A booking system for tours and events allowing you to manage bookings, check-ins, and include a booking form on your website.',
  ogImage: '/robotkonglogo.svg',
})

const { gtag, initialize } = useGtag()

const selectedOfferingIdFromLanguage = ref(null)

const breakpoints = useBreakpoints(breakpointsTailwind)
const client = useSupabaseClient()
const user = useSupabaseUser()
const stripeId = ref('')

async function initiateStripeProcedure(bookingId: string) {
  const { data } = await useFetch('/api/payments/stripe/sessions/create', {
    method: 'post',
    body: {
      stripeAccountId: stripeId.value,
      stripePriceId: selectedOfferingObject.value?.stripe_price_id || '',
      quantity:
        selectedOfferingObject.value.pay_scheme === PayScheme.TOTAL
          ? 1
          : guestCount.value,
      bookingId,
    },
  })
  if (data?.value?.session?.url) {
    window.parent.location.href = data.value.session.url
  }
}

const isCommentFieldOpen = ref(false)
const commentFieldRef = ref(null)
const comments = ref('')
const toggleCommentField = async () => {
  isCommentFieldOpen.value = !isCommentFieldOpen.value
  if (isCommentFieldOpen.value) {
    await nextTick()
    commentFieldRef.value?.focus()
  }
}

const mdOrSmaller = breakpoints.smallerOrEqual('md')
// const bar = ref(null)
// const handleFoo = () => {
//   bar.value.click()
// }

const offeringSelection = ref()
const nameTemplateRef = ref()
const emailTemplateRef = ref()
const countryTemplateRef = ref()
const makeBookingTemplateRef = ref()
const selectedDateObject = ref(DateTime.now())
const hasLoadedOfferingsOnce = ref(false)
const selectedOffering = ref(null)
const name = ref('')
const email = ref('')
const phone = ref('')
const country = ref(null)
const freeTourCommunityMemberReferrer = ref(null)
const guestCount = ref(1)
const guestCountChildren = ref(0)
const isOpenCalendar = ref(true)
const apiErrors = ref('')
const apiData = ref('')
const showDateInput = ref(false)
const selectOfferingInput = ref(null)
const nameInput = ref(null)
const submitting = ref(false)
const externalProvider = ref('')
const isAdmin = ref(route.query.adminbookings === 'jkwer3445iou7878jh')
const hasAttemptedSubmit = ref(false)

const isPhoneEnabled = ref(false)
const isCountryEnabled = ref(false)
const isPhoneRequired = ref(false)
const isPhoneInvalid = computed(() => {
  return (
    isPhoneRequired.value && hasAttemptedSubmit.value && phone.value?.length < 6
  )
})
const phoneErrors = computed(() => {
  return isPhoneInvalid.value
    ? [{ $uid: '123', $message: t('Please enter a valid phone number') }]
    : []
})

const scopedLanguage = computed(() => {
  if (route.query.scopelang == 1) {
    return locale.value
  }
  return route.query.scopelang || null
})
const scopedEvent = computed(() =>
  route.query.event ? route.query.event - 2000 : null,
)

const isEmailRequired = computed(
  () => !(isAdmin.value && externalProvider.value),
)

watch(selectedOffering, async (newVal) => {
  await nextTick()
  selectedOfferingIdFromLanguage.value = null
  if (maxSeatsPerBooking.value && guestCount.value > maxSeatsPerBooking.value) {
    guestCount.value = 1
  }
})

watch(guestCountChildren, async (newVal) => {
  await nextTick()
  if (selectedOfferingObject.value?.children_count_toward_max_seats) {
    const maxTotal = maxSeatsPerBooking.value || 1
    const maxAdults = maxTotal - newVal
    if (guestCount.value > maxAdults) {
      guestCount.value = Math.max(1, maxAdults)
    }
  }
})

watch(guestCount, async (newVal) => {
  await nextTick()
  if (selectedOfferingObject.value?.children_count_toward_max_seats) {
    const maxTotal = maxSeatsPerBooking.value || 1
    const maxChildren = maxTotal - newVal
    if (guestCountChildren.value > maxChildren) {
      guestCountChildren.value = maxChildren
    }
  }
})

watch(selectedOfferingIdFromLanguage, async (newVal) => {
  await nextTick()
  guestCount.value = 1
  locale.value =
    offerings.value?.find((offering) => offering.id === newVal)?.language ||
    'en'
})

const hydrateOfferingsForDate = async (date: DateTime) => {
  const dateStr = date.toFormat('yyyy-MM-dd')

  let query = client
    .rpc('company_offerings', { company: route.params.operator as string })
    .select(
      `
        id,
        stripe_price_id,
        seats,
        language,
        date,
        time,
        enabled,
        max_seats_per_booking,
        price,
        currency,
        pay_scheme,
        payment_link,
        event_id,
        children_count_toward_total_spaces,
        children_count_toward_max_seats,
        bookings (
          seats,
          deleted_at
        ),
        tour: events (
          id,
          parent_event_id,
          title,
          duration,
          subtitle,
          seats,
          max_seats_per_booking,
          description,
          notes_for_guests,
          meeting_point_description,
          meeting_point_address,
          website,
          meeting_point_map_link,
          trusted_partner,
          event_details!event_details_event_id_fkey(language, title, subtitle, description, is_event_default, meeting_point_address),
          company: companies (
            name,
            max_seats_per_booking,
            support_email
          )
        )
      `,
    )
    .eq('date', dateStr)
    .eq('enabled', true)

  if (scopedLanguage.value) {
    query = query.eq('language', scopedLanguage.value)
  }
  if (scopedEvent.value) {
    query = query.eq('event_id', scopedEvent.value)
  }

  const { data, error } = await query.order('time')

  if (!error && data) {
    // For each offering in our current offerings.value array
    offerings.value = offerings.value.map((offering) => {
      // Find if there's updated data for this offering
      const updatedOffering = data.find(
        (newOffering) => newOffering.id === offering.id,
      )
      // If we found updated data, return it, otherwise keep the existing offering
      return updatedOffering || offering
    })
  }
}

const isHydratingOfferings = ref(false)
const handleSelectedDateObject = async (modelData) => {
  isHydratingOfferings.value = true
  await hydrateOfferingsForDate(modelData)
  isHydratingOfferings.value = false
  isOpenCalendar.value = false
  selectedOffering.value = null
  selectedOfferingIdFromLanguage.value = null
  selectedDateObject.value = modelData
  if (
    offeringsOnSelectedDate.value?.length === 1 &&
    offeringsOnSelectedDate.value[0]
  ) {
    selectedOffering.value = offeringsOnSelectedDate.value[0].id
  }
  await nextTick()
  await document.getElementById(nameInput.value?.node?.context?.id)?.blur()
}

const maxSeatsPerBooking = computed(() => {
  if (isAdmin.value) {
    return 40
  }

  const offeringToUse = selectedOfferingIdFromLanguage.value
    ? offerings.value?.find(
        (offering) => offering.id === selectedOfferingIdFromLanguage.value,
      )
    : selectedOfferingObject.value

  if (!offeringToUse) {
    return 6
  }

  const effectiveMaxSeats =
    offeringToUse?.max_seats_per_booking ||
    offeringToUse?.tour?.max_seats_per_booking ||
    offeringToUse?.tour?.company?.max_seats_per_booking ||
    20

  // Calculate total booked seats
  const totalBookedSeats =
    offeringToUse?.bookings
      ?.filter((booking) => booking.deleted_at === null)
      ?.map((booking) => booking.seats)
      ?.reduce((partialSum, a) => partialSum + a, 0) || 0

  const offeringSeats = offeringToUse?.seats || effectiveMaxSeats

  // Return the minimum between effectiveMaxSeats and remaining seats
  return Math.max(
    1,
    Math.min(effectiveMaxSeats, offeringSeats - totalBookedSeats),
  )
})

// Add new computed properties for available options
const availableAdultOptions = computed(() => {
  const maxTotal = maxSeatsPerBooking.value || 1
  const selectedChildren = guestCountChildren.value || 0
  const maxAdults = selectedOfferingObject.value
    ?.children_count_toward_max_seats
    ? maxTotal - selectedChildren
    : maxTotal

  return Array.from({ length: maxTotal }, (_, i) => ({
    value: i + 1,
    label: i + 1,
  }))
})

const availableChildrenOptions = computed(() => {
  const maxTotal = maxSeatsPerBooking.value || 1
  const selectedAdults = guestCount.value || 1
  const maxChildren = selectedOfferingObject.value
    ?.children_count_toward_max_seats
    ? maxTotal - selectedAdults
    : maxTotal

  return Array.from({ length: maxChildren + 1 }, (_, i) => ({
    value: i,
    label: i,
  }))
})

const guestCountValidation = computed(
  () => `required|between:1,${maxSeatsPerBooking.value}`,
)

const countryOptions = computed(() =>
  countries?.map((country) => ({ label: country.name, value: country.code })),
)

const freeTourCommunityMembers = ref([
  { label: `- ${t('None')} -`, value: 'none' },
])
const hasFetchedFreeTourCommunityMembers = ref(false)
const fetchFreeTourCommunityMembers = async () => {
  if (hasFetchedFreeTourCommunityMembers.value) {
    return
  }
  freeTourCommunityMembers.value = [{ label: 'Loading...', value: '' }]
  const { data } = await client
    .from('companies')
    .select(
      `name, id, city, organisations!inner(
          name,
          viewer_token
        )`,
    )
    .eq('organisations.viewer_token', route.params.organisation as string)
    .order('city')
  freeTourCommunityMembers.value = [
    { label: `- ${t('None')} -`, value: 'none' },
    ...(data
      ?.filter((member) => ![50, 41].includes(member.id))
      ?.map((member) => ({
        label: `${member.city || member.name}`,
        value: member.id,
      })) || []),
  ]
  hasFetchedFreeTourCommunityMembers.value = true
}
const friendlySelectedDate = computed(() => {
  if (selectedDateAsDateTimeObject.value === null) {
    return null
  }
  return `${t(
    selectedDateAsDateTimeObject.value.toFormat('cccc'),
  )},  ${selectedDateAsDateTimeObject.value.toFormat('d')} ${t(
    selectedDateAsDateTimeObject.value.toFormat('LLL'),
  )} ${selectedDateAsDateTimeObject.value.toFormat('yyyy')}`
})
const selectedDateAsDateTimeObject: null | DateTime = computed(() => {
  return selectedDateObject.value
})
const offeringsOnSelectedDate = computed(() => {
  if (selectedDateAsDateTimeObject.value === null) {
    return []
  }
  return offeringsGroupedByDate.value[
    selectedDateAsDateTimeObject.value.toFormat('yyyy-LL-dd')
  ]
})

const offeringsOnSelectedDateInput = computed(() => {
  const returnOfferings = offeringsOnSelectedDate.value?.map((offering) => {
    const eventDetails =
      offering.tour?.event_details?.find(
        (eventDetail) => eventDetail.language === locale.value,
      ) ||
      offering.tour?.event_details?.find(
        (eventDetail) => eventDetail.is_event_default,
      ) ||
      offering.tour?.event_details?.[0]

    return {
      eventId: offering?.tour?.id,
      parentEventId: offering?.tour?.parent_event_id,
      date: offering?.date,
      time: offering?.time,
      value: offering?.id,
      description: eventDetails?.description,
      label: `${offering?.time?.slice(0, -3) ?? ''} ${eventDetails?.title ?? ''}`,
      subLabel: eventDetails?.subtitle,
      duration: offering?.tour?.duration,
      attrs: {
        disabled: new Date(`${offering?.date} ${offering?.time}`) < new Date(),
      },
      trustedPartner: offering?.tour?.trusted_partner,
      languageMapping: [
        {
          offeringId: offering?.id,
          language: offering?.language,
        },
      ],
      languages: [langs.where('1', offering?.language)?.['local']],
      language: offering?.language,
      price: parseFloat((offering?.price ?? 0) / 100),
      currency: offering?.currency,
      pay_scheme: offering?.pay_scheme,
      meeting_point_address: eventDetails?.meeting_point_address,
    }
  })

  const groupedByDateTime = []
  returnOfferings?.forEach((offering) => {
    const offeringWithSameDateTime = groupedByDateTime?.find(
      (offeringToCheck) =>
        offeringToCheck.eventId === offering.eventId &&
        offeringToCheck.date === offering.date &&
        offeringToCheck.time === offering.time,
    )
    if (offeringWithSameDateTime) {
      offeringWithSameDateTime.languageMapping.push({
        offeringId: offering.value,
        language: offering.language,
      })
      offeringWithSameDateTime.languages.push(...offering.languages)
    } else {
      groupedByDateTime.push(offering)
    }
  })
  // console.log({ groupedByDateTime })
  return groupedByDateTime
})

const selectedGroupedOffering = computed(() => {
  return offeringsOnSelectedDateInput.value?.find(
    (offering) => offering.value === selectedOffering.value,
  )
})

const selectedOfferingLanguages = computed(() => {
  return selectedGroupedOffering.value?.languageMapping?.map((languageMap) => ({
    label: langs.where('1', languageMap.language)?.['local'],
    value: languageMap.offeringId,
  }))
})

const internalOfferingsOnSelectedDateInput = computed(() =>
  offeringsOnSelectedDateInput.value?.filter(
    (offering) => !offering.trustedPartner,
  ),
)

const partnerOfferingsOnSelectedDateInput = computed(() =>
  offeringsOnSelectedDateInput.value?.filter(
    (offering) => !!offering.trustedPartner,
  ),
)

const selectOffering = (offeringId) => {
  const offering = offerings.value?.find((offering) => {
    return offering.id === offeringId
  })
  if (offering) {
    selectedDateObject.value = DateTime.fromFormat(offering.date, 'yyyy-LL-dd')
    isOpenCalendar.value = false
    // jumpToCorrectFormStep()
    // selectedDateObject.value = offeringDatetime
    // datepicker.value.updateInternalModelValue(new Date(offeringDatetime))
    // isOpenCalendar.value = false
  }
  selectedOffering.value = offeringId
  selectedOfferingIdFromLanguage.value = offeringId
}

const selectedOfferingObject = computed(() => {
  if (selectedOffering.value === null) {
    return null
  }
  return offerings.value?.find((offering) => {
    return offering.id === selectedOffering.value
  })
})

// const selectedOfferingDetailsToUse = computed(() => {
//   return (
//     selectedOfferingObject.value?.event_details?.find(
//       (eventDetail) => eventDetail.language === locale.value
//     ) ||
//     selectedOfferingObject.value?.event_details?.find(
//       (eventDetail) => eventDetail.is_event_default
//     ) ||
//     selectedOfferingObject.value?.event_details?.[0]
//   )
// })

const isSelectedOfferingFull = computed(() => {
  const offeringToUse =
    selectedOfferingLanguages.value?.length === 1
      ? selectedOfferingObject.value
      : offerings.value?.find(
          (offering) => offering.id === selectedOfferingIdFromLanguage.value,
        )

  if (!offeringToUse) {
    return false
  }

  // Calculate total booked seats based on children_count_toward_total_spaces
  const bookingsCount =
    offeringToUse?.bookings
      ?.filter((booking) => booking.deleted_at === null)
      ?.map((booking) => {
        // If children don't count toward total spaces, only count adults
        if (!offeringToUse.children_count_toward_total_spaces) {
          return booking.seats
        }
        // If they do count, include both adults and children
        return (booking.seats || 0) + (booking.children || 0)
      })
      ?.reduce((partialSum, a) => partialSum + a, 0) || 0

  return bookingsCount >= (offeringToUse?.seats || 0)
})
// Check for offerings falling on today's date, and find the latest offering datetime
const getLatestOfferingOnDate = (date: Date) => {
  const offeringsToday =
    offeringDates.value?.filter((date: Date) => {
      return (
        new Date(date.getTime()).setHours(0, 0, 0, 0) ===
        new Date().setHours(0, 0, 0, 0)
      )
    }) ?? []
  return new Date(Math.max(...offeringsToday))
}

const isDateDisabled = (dateToAssess: Date) => {
  // Disable all dates which have already passed
  if (new Date().setHours(0, 0, 0, 0) > dateToAssess.getTime()) {
    return true
  }
  const dateToAssessAsString =
    DateTime.fromJSDate(dateToAssess).toFormat('yyyy-LL-dd')
  const currentDateAsString = DateTime.now().toFormat('yyyy-LL-dd')
  const dateHasOfferings =
    datesWithOfferings.value.includes(dateToAssessAsString)
  if (dateHasOfferings) {
    if (currentDateAsString == dateToAssessAsString) {
      if (new Date() > getLatestOfferingOnDate(dateToAssess)) {
        return true
      }
    }
    return false
  }
  return true
}

const availableOfferings = computed(() => {
  if (isAdmin.value) {
    return offerings.value
  }
  return (
    offerings.value?.filter(
      (offering) =>
        DateTime.fromFormat(
          `${offering.date} ${offering.time}`,
          'yyyy-MM-dd HH:mm:ss',
        ) > DateTime.now(),
    ) ??
    /* .filter(offering => {
      const bookingsCount = offering.bookings?.map(booking => booking.seats)?.reduce((partialSum, a) => partialSum + a, 0) || 0
      return bookingsCount < offering.seats
    }) */ []
  )
})

const offeringDates = computed(() => {
  // array of all offering date times as Date objects
  return availableOfferings.value?.map(
    (offering) => new Date(`${offering.date} ${offering.time}`),
  )
})

// All unique dates with offerings
const datesWithOfferings = computed(() => {
  const dates = lightQueryOfferings.value
    ?.filter((offering) => {
      const offeringDateTime = DateTime.fromFormat(
        `${offering.date} ${offering.time}`,
        'yyyy-MM-dd HH:mm:ss',
      )
      return offeringDateTime > DateTime.now()
    })
    ?.filter((offering) => offering.enabled)
    ?.map((offering) => offering.date)

  return dates?.length === 0 ? [] : [...new Set(dates)]
})
const offeringsGroupedByDate = computed(() => {
  const offeringsByDate = {}
  for (const dateWithOffering of datesWithOfferings.value) {
    offeringsByDate[dateWithOffering] =
      availableOfferings.value?.filter((offering) => {
        return offering.date === dateWithOffering
      }) ?? []
  }
  return offeringsByDate
})
const handleOfferingSelectionChange = () => {
  // jumpToCorrectFormStep()
}

const jumpToCorrectFormStep = () => {
  // if (!name.value) {
  //   nameTemplateRef.value.inputTemplateRef.focus()
  // } else if (!email.value) {
  //   emailTemplateRef.value.inputTemplateRef.focus()
  // } else if (!guestCount.value) {
  //   guestCountTemplateRef.value.selectTemplateRef.focus()
  // } else if (!country.value) {
  //   countryTemplateRef.value.selectTemplateRef.focus()
  // }
}
const offerings = ref([])
const fetchingOfferings = ref(true)

const googleAnalyticsId = ref('')

const fetchCompanySettings = async () => {
  const { data, error } = await client
    .from('companies')
    .select(
      `
        enable_phone_number_field_on_bookings,
        require_phone_number_field_on_bookings,
        enable_country_field_on_bookings,
        stripe_connect_id,
        organisations!inner(
          google_analytics_id
        )
      `,
    )
    .eq('viewer_token', route.params.operator)
    .limit(1)
    .single()
  if (error) {
    // console.error(error)
  } else if (data) {
    isPhoneEnabled.value = data.enable_phone_number_field_on_bookings
    isCountryEnabled.value = data.enable_country_field_on_bookings
    isPhoneRequired.value = data.require_phone_number_field_on_bookings
    stripeId.value = data.stripe_connect_id
    googleAnalyticsId.value = data.organisations[0]?.google_analytics_id || ''
    // if (googleAnalyticsId.value) {
    //   initialize(googleAnalyticsId.value)
    //   gtag('config', googleAnalyticsId.value, {
    //     linker: {
    //       domains: ['freetourcommunity.com', 'robotkong.com'],
    //     },
    //   })
    // }
    // initialize('G-LP6XSM9GNB')
  }
}
if (route.params.organisation !== 'beta') {
  initialize('G-C21QMQR8L6')
  gtag('config', 'G-C21QMQR8L6', {
    linker: {
      domains: ['freetourcommunity.com', 'robotkong.com'],
    },
  })
}
initialize('G-LP6XSM9GNB')

const lightQueryOfferings = ref([])

const fetchOfferings = async ({
  month,
  year,
  isFirstLoad = false,
}: { month?: number; year?: number; isFirstLoad?: boolean } = {}) => {
  const hasYearAndMonthProvided = month && year
  fetchingOfferings.value = true

  // Calculate query date ranges based on current date and provided month/year
  const now = DateTime.now()
  const currentMonth = now.month
  const currentYear = now.year

  let startDate: DateTime
  let endDate: DateTime

  if (
    !hasYearAndMonthProvided ||
    (month === currentMonth && year === currentYear)
  ) {
    // Initial load or current month - check from today through next month + 6 days
    startDate = now.startOf('day')
    endDate = now.plus({ months: 1, days: 6 }).endOf('day')
  } else {
    // Other months - include 6 days before and after
    startDate = DateTime.fromObject({ year, month })
      .minus({ days: 6 })
      .startOf('day')
    endDate = DateTime.fromObject({ year, month })
      .plus({ months: 1, days: 6 })
      .endOf('day')
  }

  let lightQuery = client
    .rpc('company_offerings', { company: route.params.operator })
    .select(
      `
        id,
        date,
        time,
        enabled,
        event_id,
        language
      `,
    )
    .gte('date', startDate.toFormat('yyyy-MM-dd'))
    .lte('date', endDate.toFormat('yyyy-MM-dd'))
    .eq('enabled', true)

  if (scopedLanguage.value) {
    lightQuery = lightQuery.eq('language', scopedLanguage.value)
  }
  if (scopedEvent.value) {
    lightQuery = lightQuery.eq('event_id', scopedEvent.value)
  }

  lightQuery = lightQuery.order('date').order('time')

  const { data: lightQueryData, error: lightQueryError } = await lightQuery
  lightQueryOfferings.value = lightQueryData
  offerings.value = lightQueryData

  // Only try 3-month fallback on first load
  if (isFirstLoad && (!lightQueryData || lightQueryData?.length === 0)) {
    const threeMonthEndDate = now.plus({ months: 4 }).endOf('day')
    let query = client
      .rpc('company_offerings', { company: route.params.operator })
      .select(
        `
        id,
        date,
        time,
        enabled,
        event_id,
        language
      `,
      )
      .gte('date', startDate.toFormat('yyyy-MM-dd'))
      .lte('date', threeMonthEndDate.toFormat('yyyy-MM-dd'))
      .eq('enabled', true)

    if (scopedLanguage.value) {
      query = query.eq('language', scopedLanguage.value)
    }
    if (scopedEvent.value) {
      query = query.eq('event_id', scopedEvent.value)
    }

    const { data: extendedData } = await query.order('date').order('time')

    if (extendedData?.length) {
      // Found offerings in next 3 months
      const firstAvailableDate = DateTime.fromFormat(
        extendedData[0].date,
        'yyyy-MM-dd',
      )

      // Calculate the date range for the month where offerings were found
      const startDate = DateTime.fromObject({
        year: firstAvailableDate.year,
        month: firstAvailableDate.month,
      })
        .minus({ days: 6 })
        .startOf('day')
      const endDate = DateTime.fromObject({
        year: firstAvailableDate.year,
        month: firstAvailableDate.month,
      })
        .plus({ months: 1, days: 6 })
        .endOf('day')

      // Fetch offerings for the calculated date range
      let query = client
        .rpc('company_offerings', { company: route.params.operator })
        .select(
          `
          id,
          date,
          time,
          enabled,
          event_id,
          language
        `,
        )
        .gte('date', startDate.toFormat('yyyy-MM-dd'))
        .lte('date', endDate.toFormat('yyyy-MM-dd'))
        .eq('enabled', true)

      if (scopedLanguage.value) {
        query = query.eq('language', scopedLanguage.value)
      }
      if (scopedEvent.value) {
        query = query.eq('event_id', scopedEvent.value)
      }

      const { data } = await query.order('date').order('time')
      lightQueryOfferings.value = data
      offerings.value = data
      fetchingOfferings.value = false
      calendarBaseDateTime.value = DateTime.fromFormat(
        extendedData[0].date,
        'yyyy-MM-dd',
      )
      return
    }
  }

  fetchingOfferings.value = false
}

const fetchUpdatedOfferings = async ({ month, year }) => {
  await fetchOfferings({ month, year })
}

onMounted(async () => {
  hasLoadedOfferingsOnce.value = false
  await fetchOfferings({
    month: DateTime.now().month,
    year: DateTime.now().year,
    isFirstLoad: true,
  })
  if (offerings.value?.length === 0) {
    await fetchOfferings({ isFirstLoad: true })
  }
  hasLoadedOfferingsOnce.value = true
  await fetchCompanySettings()
})

const isViewingFromOrganisation = computed(() => {
  return route.params.organisation !== 'beta'
})

const rules = computed(() => ({
  name: {
    required: helpers.withMessage(t('Please add your name'), requiredValidator),
  },
  selectedOffering: {
    required: helpers.withMessage(t('Please choose a tour'), requiredValidator),
  },
  selectedLanguage: {
    required: helpers.withMessage(
      t('Please choose a language'),
      (value) =>
        selectedOfferingLanguages.value?.length > 1
          ? !!selectedOfferingIdFromLanguage.value
          : true,
      // selectedOfferingLanguages.value?.length > 1 ? false : true
    ),
  },
  selectedDateObject: {
    required: helpers.withMessage(
      t('Please choose a date first'),
      requiredValidator,
    ),
  },
  email: isEmailRequired.value
    ? {
        required: helpers.withMessage(
          t('Please add your email'),
          requiredValidator,
        ),
        email: helpers.withMessage(
          t('Please enter a valid email address'),
          emailValidator,
        ),
      }
    : {},
  freeTourCommunityMemberReferrer: isViewingFromOrganisation.value
    ? {
        required: helpers.withMessage(
          t('Please select a referral member of Free Tour Community or "None"'),
          requiredValidator,
        ),
      }
    : {},
}))

const v$ = useVuelidate(rules, {
  name,
  email,
  selectedOffering,
  selectedDateObject,
  selectedLanguage: selectedOfferingIdFromLanguage,
  freeTourCommunityMemberReferrer,
})

const touchDateAndTourFields = () => {
  v$.value.selectedOffering?.$touch()
  v$.value.selectedDateObject?.$touch()
}

const handleSubmit = async () => {
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) return
  hasAttemptedSubmit.value = true
  await nextTick()
  if (isPhoneRequired.value && isPhoneInvalid.value) {
    return
  }
  submitting.value = true
  const { data: submitData, error } = await client
    .from('bookings')
    .upsert({
      organisation_viewer_token:
        route.params.organisation !== 'beta'
          ? route.params.organisation
          : undefined,
      offering_id: selectedOfferingIdFromLanguage.value
        ? selectedOfferingIdFromLanguage.value
        : selectedOffering.value,
      name: name.value,
      email: email.value,
      phone: phone.value?.length < 5 ? '' : phone.value,
      country: country.value,
      seats: guestCount.value,
      children: guestCountChildren.value,
      external_provider: externalProvider.value,
      comments: comments.value,
      referrer: typeof rkRef.value === 'string' && ['ig'].includes(rkRef.value)
        ? rkRef.value
        : undefined,
      referrer_company_id:
        freeTourCommunityMemberReferrer.value &&
        freeTourCommunityMemberReferrer.value !== 'none'
          ? freeTourCommunityMemberReferrer.value
          : undefined,
    })
    .select(
      `
        id
      `,
    )
    .limit(1)
    .single()
  if (error) {
    submitting.value = false
    // console.error(error)
  } else if (submitData.id) {
    gtag('event', 'submit', {
      event_category: 'Booking',
      event_label: 'Tour Booking',
      value: 1,
      num_adults: parseInt(guestCount.value, 10),
      num_children: parseInt(guestCountChildren.value, 10),
      num_total:
        parseInt(guestCount.value, 10) + parseInt(guestCountChildren.value, 10),
      country: country.value,
      referrer: rkRef.value,
      referrer_company_id: freeTourCommunityMemberReferrer.value,
      referrer_company_city: freeTourCommunityMemberReferrer.value
        ? freeTourCommunityMembers.value?.find(
            (member) => member.value == freeTourCommunityMemberReferrer.value,
          )?.label
        : 'none',
    })
    if (selectedOfferingObject.value?.price > 0) {
      if (!!selectedOfferingObject.value?.stripe_price_id) {
        await initiateStripeProcedure(submitData.id)
      } else {
        await navigateTo(`/booking/isolated/payment/${submitData.id}`)
      }
    } else {
      await navigateTo(`/booking/isolated/view/${submitData.id}`)
    }
  }
}

const showNoOfferingsFoundMessage = computed(() => {
  return (
    !fetchingOfferings.value &&
    lightQueryOfferings.value?.length === 0 &&
    hasLoadedOfferingsOnce.value
  )
})
</script>
<style>
@tailwind base;

.left-col,
.right-col {
  @apply w-[400px];
  @apply max-w-[400px];
}

.n-collapse-item-arrow {
  display: none !important;
}

.dp__cell_inner {
  padding: 0 !important;
}

.datepicker-container {
  min-width: 260px;
  min-height: 260px;
}

.dp__outer_menu_wrap {
  max-width: 100% !important;
}

@media only screen and (min-width: 330px) {
  .dp__cell_inner,
  .dp__calendar_header_item {
    width: 45px !important;
  }

  .datepicker-container {
    min-width: 317px;
  }

  .dp__main {
    max-width: 317px !important;
  }
}

@media only screen and (min-width: 403px) {
  .dp__cell_inner,
  .dp__calendar_header_item {
    width: 50px !important;
  }

  .datepicker-container {
    min-width: 350px;
  }

  .dp__main {
    max-width: 350px !important;
  }
}

.dp__instance_calendar {
}

.dp__menu.dp__relative {
  border-color: #94929c !important;
}

.n-collapse-item__header-main {
  display: inline-block !important;
}

.n-collapse-item__content-inner {
  padding-top: 0 !important;
}

.submit-button {
  width: 100% !important;
  margin: 0 !important;
}

.margin-0 {
  margin: 0 !important;
}

.n-base-selection__border {
  border: none !important;
  box-shadow: 0 0 0 1px #94929c;
  padding-top: 10.5px;
  padding-bottom: 10.5px;
}

.dp__calendar_item {
  margin: -0.5px;
}

.dp__today {
  border: none !important;
  @apply bg-blue-50;
}

.dp__date_hover:hover {
  @apply bg-gray-200 !important;
}

.n-base-selection {
  padding-top: 4px;
  padding-bottom: 4px;
}

input,
input *,
select,
select *,
.n-base-selection * {
  font-size: 16px !important;
}

.formkit-help {
  @apply text-sm;
}

/*svg {*/
/*  height: 1rem;*/
/*  width: 1rem;*/
/*}*/
</style>
